/**
 * Namespace-Specific Logger Configurations
 * 
 * Pre-configured settings for each namespace with specialized behavior,
 * default metadata, filtering rules, and performance thresholds.
 */

import { LogNamespace } from './types';
import {
  NamespaceConfiguration,
  ContextProvider,
  NamespaceContext
} from './logger-factory';

/**
 * Default configurations for each namespace
 */
export const NAMESPACE_CONFIGURATIONS: Record<LogNamespace, NamespaceConfiguration> = {
  [LogNamespace.AUTH]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'password',
      'token',
      'secret',
      'credential',
      'session',
      'cookie',
      'authorization',
      'bearer',
      'jwt',
      'refresh_token',
      'access_token',
      'api_key',
      'private_key',
      'salt',
      'hash'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: true,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['authMethod', 'loginAttempt', 'securityLevel']
    },
    performanceThresholds: {
      operationWarningMs: 500,  // Auth operations should be fast
      operationErrorMs: 2000,   // Critical for user experience
      memoryWarningMB: 50,
      memoryErrorMB: 200
    }
  },

  [LogNamespace.API]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'api_key',
      'authorization',
      'bearer',
      'x-api-key',
      'secret',
      'token'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['endpoint', 'method', 'statusCode', 'responseTime', 'clientId']
    },
    performanceThresholds: {
      operationWarningMs: 1000,
      operationErrorMs: 5000,
      memoryWarningMB: 100,
      memoryErrorMB: 500
    }
  },

  [LogNamespace.UI]: {
    defaultLogLevel: 'warn',  // Reduce noise in UI logs
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'password',
      'email',
      'phone',
      'ssn',
      'credit_card',
      'personal_info'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: true,
      includeRequestId: false,
      includeTimestamp: true,
      customFields: ['component', 'action', 'page', 'userAgent', 'viewport']
    },
    performanceThresholds: {
      operationWarningMs: 100,  // UI should be very responsive
      operationErrorMs: 500,
      memoryWarningMB: 200,
      memoryErrorMB: 1000
    }
  },

  [LogNamespace.PERFORMANCE]: {
    defaultLogLevel: 'debug',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: false,  // Performance logs usually don't contain sensitive data
    customFilterRules: [],
    contextInjection: {
      enabled: true,
      includeUserId: false,
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['operation', 'duration', 'memoryUsage', 'cpuUsage', 'cacheHitRate']
    },
    performanceThresholds: {
      operationWarningMs: 10,   // Performance logging should be minimal overhead
      operationErrorMs: 50,
      memoryWarningMB: 10,
      memoryErrorMB: 50
    }
  },

  [LogNamespace.DATABASE]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'password',
      'email',
      'phone',
      'ssn',
      'credit_card',
      'personal_info',
      'connection_string',
      'database_url',
      'db_password'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['query', 'table', 'operation', 'rowsAffected', 'executionTime']
    },
    performanceThresholds: {
      operationWarningMs: 1000,
      operationErrorMs: 10000,  // Database operations can be slower
      memoryWarningMB: 200,
      memoryErrorMB: 1000
    }
  },

  [LogNamespace.CLASSIFICATION]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'prompt',
      'response',
      'api_key',
      'model_key',
      'personal_info'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['modelId', 'promptLength', 'category', 'confidence', 'processingTime']
    },
    performanceThresholds: {
      operationWarningMs: 2000,  // AI operations can be slower
      operationErrorMs: 10000,
      memoryWarningMB: 500,
      memoryErrorMB: 2000
    }
  },

  [LogNamespace.JUDGE]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'prompt',
      'response',
      'api_key',
      'model_key',
      'personal_info',
      'evaluation_data'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['judgeModel', 'evaluationId', 'criteria', 'score', 'reasoning']
    },
    performanceThresholds: {
      operationWarningMs: 5000,  // Judging can be complex
      operationErrorMs: 30000,
      memoryWarningMB: 500,
      memoryErrorMB: 2000
    }
  },

  [LogNamespace.STORAGE]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'access_key',
      'secret_key',
      'connection_string',
      'storage_url',
      'personal_info',
      'file_content'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['bucket', 'fileName', 'fileSize', 'operation', 'storageProvider']
    },
    performanceThresholds: {
      operationWarningMs: 2000,
      operationErrorMs: 10000,
      memoryWarningMB: 100,
      memoryErrorMB: 500
    }
  },

  [LogNamespace.WORKER]: {
    defaultLogLevel: 'info',
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'api_key',
      'token',
      'credential',
      'personal_info'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: false,  // Workers might not have user context
      includeSessionId: false,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['jobId', 'queueName', 'priority', 'retryCount', 'processingTime']
    },
    performanceThresholds: {
      operationWarningMs: 5000,   // Background jobs can be slower
      operationErrorMs: 60000,    // Allow long-running jobs
      memoryWarningMB: 200,
      memoryErrorMB: 1000
    }
  },

  [LogNamespace.METRICS]: {
    defaultLogLevel: 'debug',
    enablePerformanceTracking: false,  // Metrics logging shouldn't track its own performance
    enableSensitiveDataFiltering: false,  // Metrics usually don't contain sensitive data
    customFilterRules: [],
    contextInjection: {
      enabled: true,
      includeUserId: false,
      includeSessionId: false,
      includeRequestId: false,
      includeTimestamp: true,
      customFields: ['metricName', 'value', 'unit', 'tags', 'aggregation']
    },
    performanceThresholds: {
      operationWarningMs: 10,   // Metrics collection should be very fast
      operationErrorMs: 100,
      memoryWarningMB: 50,
      memoryErrorMB: 200
    }
  },

  [LogNamespace.SECURITY]: {
    defaultLogLevel: 'warn',  // Security logs should be important
    enablePerformanceTracking: true,
    enableSensitiveDataFiltering: true,
    customFilterRules: [
      'password',
      'token',
      'secret',
      'credential',
      'session',
      'cookie',
      'authorization',
      'personal_info',
      'ip_address',  // Might be considered sensitive depending on regulations
      'user_agent'
    ],
    contextInjection: {
      enabled: true,
      includeUserId: true,
      includeSessionId: true,
      includeRequestId: true,
      includeTimestamp: true,
      customFields: ['threatLevel', 'riskScore', 'securityEvent', 'sourceIp', 'actionTaken']
    },
    performanceThresholds: {
      operationWarningMs: 100,  // Security logging should be fast
      operationErrorMs: 1000,
      memoryWarningMB: 50,
      memoryErrorMB: 200
    }
  }
};

/**
 * Namespace-specific context providers
 */
export const NAMESPACE_CONTEXT_PROVIDERS: Record<LogNamespace, ContextProvider[]> = {
  [LogNamespace.AUTH]: [
    {
      name: 'AuthContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        authMethod: context.customMetadata.authMethod || 'unknown',
        securityLevel: context.customMetadata.securityLevel || 'standard',
        loginAttempt: context.customMetadata.loginAttempt || false
      })
    }
  ],

  [LogNamespace.API]: [
    {
      name: 'ApiContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        endpoint: context.customMetadata.endpoint || 'unknown',
        method: context.customMetadata.method || 'unknown',
        statusCode: context.customMetadata.statusCode,
        clientId: context.customMetadata.clientId
      })
    }
  ],

  [LogNamespace.UI]: [
    {
      name: 'UiContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        component: context.customMetadata.component || 'unknown',
        action: context.customMetadata.action || 'unknown',
        page: context.customMetadata.page || window?.location?.pathname || 'unknown'
      })
    }
  ],

  [LogNamespace.PERFORMANCE]: [
    {
      name: 'PerformanceContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        operation: context.customMetadata.operation || 'unknown',
        memoryUsage: context.customMetadata.memoryUsage,
        cacheHitRate: context.customMetadata.cacheHitRate
      })
    }
  ],

  [LogNamespace.DATABASE]: [
    {
      name: 'DatabaseContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        table: context.customMetadata.table || 'unknown',
        operation: context.customMetadata.operation || 'unknown',
        rowsAffected: context.customMetadata.rowsAffected,
        executionTime: context.customMetadata.executionTime
      })
    }
  ],

  [LogNamespace.CLASSIFICATION]: [
    {
      name: 'ClassificationContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        modelId: context.customMetadata.modelId || 'unknown',
        category: context.customMetadata.category || 'unknown',
        confidence: context.customMetadata.confidence,
        promptLength: context.customMetadata.promptLength
      })
    }
  ],

  [LogNamespace.JUDGE]: [
    {
      name: 'JudgeContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        judgeModel: context.customMetadata.judgeModel || 'unknown',
        evaluationId: context.customMetadata.evaluationId,
        criteria: context.customMetadata.criteria,
        score: context.customMetadata.score
      })
    }
  ],

  [LogNamespace.STORAGE]: [
    {
      name: 'StorageContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        bucket: context.customMetadata.bucket || 'unknown',
        fileName: context.customMetadata.fileName || 'unknown',
        fileSize: context.customMetadata.fileSize,
        operation: context.customMetadata.operation || 'unknown'
      })
    }
  ],

  [LogNamespace.WORKER]: [
    {
      name: 'WorkerContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        jobId: context.customMetadata.jobId || 'unknown',
        queueName: context.customMetadata.queueName || 'default',
        priority: context.customMetadata.priority || 'normal',
        retryCount: context.customMetadata.retryCount || 0
      })
    }
  ],

  [LogNamespace.METRICS]: [
    {
      name: 'MetricsContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        metricName: context.customMetadata.metricName || 'unknown',
        value: context.customMetadata.value,
        unit: context.customMetadata.unit || 'count',
        tags: context.customMetadata.tags || {}
      })
    }
  ],

  [LogNamespace.SECURITY]: [
    {
      name: 'SecurityContextProvider',
      priority: 100,
      enabled: true,
      provide: (context: NamespaceContext) => ({
        threatLevel: context.customMetadata.threatLevel || 'low',
        riskScore: context.customMetadata.riskScore || 0,
        securityEvent: context.customMetadata.securityEvent || 'unknown',
        actionTaken: context.customMetadata.actionTaken || 'none'
      })
    }
  ]
};

/**
 * Default metadata for each namespace
 */
export const NAMESPACE_DEFAULT_METADATA: Record<LogNamespace, Record<string, any>> = {
  [LogNamespace.AUTH]: {
    domain: 'authentication',
    criticality: 'high',
    retention: '90days',
    compliance: ['SOX', 'GDPR']
  },

  [LogNamespace.API]: {
    domain: 'api',
    criticality: 'medium',
    retention: '30days',
    compliance: ['GDPR']
  },

  [LogNamespace.UI]: {
    domain: 'user-interface',
    criticality: 'low',
    retention: '7days',
    compliance: ['GDPR']
  },

  [LogNamespace.PERFORMANCE]: {
    domain: 'performance',
    criticality: 'medium',
    retention: '14days',
    compliance: []
  },

  [LogNamespace.DATABASE]: {
    domain: 'database',
    criticality: 'high',
    retention: '90days',
    compliance: ['SOX', 'GDPR', 'HIPAA']
  },

  [LogNamespace.CLASSIFICATION]: {
    domain: 'ai-classification',
    criticality: 'medium',
    retention: '30days',
    compliance: ['GDPR']
  },

  [LogNamespace.JUDGE]: {
    domain: 'ai-evaluation',
    criticality: 'medium',
    retention: '30days',
    compliance: ['GDPR']
  },

  [LogNamespace.STORAGE]: {
    domain: 'file-storage',
    criticality: 'medium',
    retention: '30days',
    compliance: ['GDPR']
  },

  [LogNamespace.WORKER]: {
    domain: 'background-jobs',
    criticality: 'medium',
    retention: '14days',
    compliance: []
  },

  [LogNamespace.METRICS]: {
    domain: 'telemetry',
    criticality: 'low',
    retention: '7days',
    compliance: []
  },

  [LogNamespace.SECURITY]: {
    domain: 'security',
    criticality: 'critical',
    retention: '365days',
    compliance: ['SOX', 'GDPR', 'HIPAA', 'PCI-DSS']
  }
};

/**
 * Factory function to create pre-configured namespace configurations
 */
export function createNamespaceConfiguration(namespace: LogNamespace): NamespaceConfiguration {
  return { ...NAMESPACE_CONFIGURATIONS[namespace] };
}

/**
 * Factory function to create pre-configured context providers for a namespace
 */
export function createNamespaceContextProviders(namespace: LogNamespace): ContextProvider[] {
  return NAMESPACE_CONTEXT_PROVIDERS[namespace].map(provider => ({ ...provider }));
}

/**
 * Factory function to create default metadata for a namespace
 */
export function createNamespaceDefaultMetadata(namespace: LogNamespace): Record<string, any> {
  return { ...NAMESPACE_DEFAULT_METADATA[namespace] };
}

/**
 * Utility to get all available namespaces with configurations
 */
export function getAvailableNamespaces(): LogNamespace[] {
  return Object.keys(NAMESPACE_CONFIGURATIONS) as LogNamespace[];
}

/**
 * Utility to validate namespace configuration
 */
export function validateNamespaceConfiguration(config: NamespaceConfiguration): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check required fields
  if (!config.contextInjection) {
    errors.push('contextInjection configuration is required');
  }

  // Validate performance thresholds
  if (config.performanceThresholds) {
    const { operationWarningMs, operationErrorMs, memoryWarningMB, memoryErrorMB } = config.performanceThresholds;
    
    if (operationWarningMs && operationErrorMs && operationWarningMs >= operationErrorMs) {
      errors.push('operationWarningMs must be less than operationErrorMs');
    }
    
    if (memoryWarningMB && memoryErrorMB && memoryWarningMB >= memoryErrorMB) {
      errors.push('memoryWarningMB must be less than memoryErrorMB');
    }
  }

  // Validate custom filter rules
  if (config.customFilterRules && !Array.isArray(config.customFilterRules)) {
    errors.push('customFilterRules must be an array');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Configuration inheritance utility
 */
export function mergeNamespaceConfigurations(
  base: NamespaceConfiguration,
  override: Partial<NamespaceConfiguration>
): NamespaceConfiguration {
  return {
    ...base,
    ...override,
    contextInjection: {
      ...base.contextInjection,
      ...override.contextInjection
    },
    performanceThresholds: {
      ...base.performanceThresholds,
      ...override.performanceThresholds
    },
    customFilterRules: override.customFilterRules || base.customFilterRules
  };
} 